import { BinanceWalletConnector } from '@pancakeswap/wagmi/connectors/binanceWallet'
import { BloctoConnector } from '@pancakeswap/wagmi/connectors/blocto'
import { TrustWalletConnector } from '@pancakeswap/wagmi/connectors/trustWallet'
import { bsc, Chain, goerli, mainnet } from 'wagmi/chains'
import { configureChains, createClient } from 'wagmi'
import memoize from 'lodash/memoize'
import { CoinbaseWalletConnector } from 'wagmi/connectors/coinbaseWallet'
import { InjectedConnector } from 'wagmi/connectors/injected'
import { MetaMaskConnector } from 'wagmi/connectors/metaMask'
import { WalletConnectConnector } from 'wagmi/connectors/walletConnect'
import { LedgerConnector } from 'wagmi/connectors/ledger'
import { jsonRpcProvider } from 'wagmi/providers/jsonRpc'
import { SafeConnector } from './safeConnector'

const long: Chain = {
  /** ID in number form */
  id: 7798, // for test use
  /** Human-readable name */
  name: 'OpenEX Long Testnet',
  /** Internal network name */
  network: 'long',
  /** Currency used by chain */
  nativeCurrency: { name: 'USD Tether', symbol: 'USDT', decimals: 18 },
  /** Collection of RPC endpoints */
  rpcUrls: {
    // main: { http: ['https://long.rpc.openex.network'], webSocket: [''] },
    main: { http: ['https://long.rpc.openex.network'], webSocket: [''] },
    default: { http: ['https://long.rpc.openex.network'], webSocket: [''] },
  },
  /** Collection of block explorers */
  blockExplorers: {
    main: { name: 'LongScan', url: 'https://scan.long.openex.network' },
    default: { name: 'main', url: 'https://scan.long.openex.network' },
  },
  /** Collection of contracts */
  contracts: {
    ensRegistry: { address: undefined },
    multicall3: { address: '0x934Db52882E39cd5B3535E5b3D57bDea01F6D09d' },
  },
  /** Flag for test networks */
  testnet: true,
}

const core: Chain = {
  /** ID in number form */
  id: 1116, // for test use
  /** Human-readable name */
  name: 'Core Chain',
  /** Internal network name */
  network: 'core',
  /** Currency used by chain */
  nativeCurrency: { name: 'CORE', symbol: 'CORE', decimals: 18 },
  /** Collection of RPC endpoints */
  rpcUrls: {
    main: { http: ['https://rpc.coredao.org'], webSocket: [''] },
    default: { http: ['https://rpc.coredao.org'], webSocket: [''] },
  },
  /** Collection of block explorers */
  blockExplorers: {
    main: { name: 'CoreScan', url: 'https://scan.coredao.org' },
    default: { name: 'main', url: 'https://scan.coredao.org' },
  },
  /** Collection of contracts */
  contracts: {
    ensRegistry: { address: undefined },
    multicall3: { address: '0x1FC24582A7d961F54be37fC3e5f09d81E626Ba44' },
  },
  /** Flag for test networks */
  testnet: false,
}

const boat: Chain = {
  /** ID in number form */
  id: 8047, // for test use
  /** Human-readable name */
  name: 'BOAT Chain',
  /** Internal network name */
  network: 'BOAT',
  /** Currency used by chain */
  nativeCurrency: { name: 'BOAT', symbol: 'BOAT', decimals: 18 },
  /** Collection of RPC endpoints */
  rpcUrls: {
    main: { http: ['https://rpc0.come.boats'], webSocket: [''] },
    default: { http: ['https://rpc0.come.boats'], webSocket: [''] },
  },
  /** Collection of block explorers */
  blockExplorers: {
    main: { name: 'BoatScan', url: 'https://scan.come.boats' },
    default: { name: 'main', url: 'https://scan.come.boats' },
  },
  /** Collection of contracts */
  contracts: {
    ensRegistry: { address: undefined },
    multicall3: { address: '0x2a7428Ce8c7A96f1A11F4a1fd2a104b9F83d6201' },
  },
  /** Flag for test networks */
  testnet: false,
}

export const CHAINS = [long, core, boat]

const getNodeRealUrl = (networkName: string) => {
  let host = null

  switch (networkName) {
    case 'homestead':
      if (process.env.NEXT_PUBLIC_NODE_REAL_API_ETH) {
        host = `eth-mainnet.nodereal.io/v1/${process.env.NEXT_PUBLIC_NODE_REAL_API_ETH}`
      }
      break
    case 'goerli':
      if (process.env.NEXT_PUBLIC_NODE_REAL_API_GOERLI) {
        host = `eth-goerli.nodereal.io/v1/${process.env.NEXT_PUBLIC_NODE_REAL_API_GOERLI}`
      }
      break
    case 'core':
      host = `rpc.coredao.org`
      break
    case 'qilin':
      host = `rpc.openex.network`
      break
    case 'long':
      host = `long.rpc.openex.network`
      break
    case 'btcs':
      host = `rpc.test.btcs.network`
      break
    default:
      host = null
  }

  if (!host) {
    return null
  }

  const url = `https://${host}`
  return {
    http: url,
    webSocket: url.replace(/^http/i, 'wss').replace('.nodereal.io/v1', '.nodereal.io/ws/v1'),
  }
}

export const { provider, chains } = configureChains(CHAINS, [
  jsonRpcProvider({
    rpc: (chain) => {
      if (!!process.env.NEXT_PUBLIC_NODE_PRODUCTION && chain.id === bsc.id) {
        return { http: process.env.NEXT_PUBLIC_NODE_PRODUCTION }
      }
      if (process.env.NODE_ENV === 'test' && chain.id === mainnet.id) {
        return { http: 'https://cloudflare-eth.com' }
      }

      return getNodeRealUrl(chain.network) || { http: chain.rpcUrls.default.http[0] }
    },
  }),
])

export const injectedConnector = new InjectedConnector({
  chains,
  options: {
    shimDisconnect: false,
    shimChainChangedDisconnect: true,
  },
})

export const coinbaseConnector = new CoinbaseWalletConnector({
  chains,
  options: {
    appName: 'LongSwap',
    appLogoUrl: 'https://pancakeswap.com/logo.png',
  },
})

export const walletConnectConnector = new WalletConnectConnector({
  chains,
  options: {
    qrcode: true,
  },
})

export const walletConnectNoQrCodeConnector = new WalletConnectConnector({
  chains,
  options: {
    qrcode: false,
  },
})

export const metaMaskConnector = new MetaMaskConnector({
  chains,
  options: {
    shimDisconnect: false,
    shimChainChangedDisconnect: true,
  },
})

const bloctoConnector = new BloctoConnector({
  chains,
  options: {
    defaultChainId: 56,
    appId: 'e2f2f0cd-3ceb-4dec-b293-bb555f2ed5af',
  },
})

const ledgerConnector = new LedgerConnector({
  chains,
})

export const bscConnector = new BinanceWalletConnector({ chains })

export const trustWalletConnector = new TrustWalletConnector({
  chains,
  options: {
    shimDisconnect: false,
    shimChainChangedDisconnect: true,
  },
})

export const client = createClient({
  autoConnect: false,
  provider,
  connectors: [
    new SafeConnector({ chains }),
    metaMaskConnector,
    injectedConnector,
    coinbaseConnector,
    walletConnectConnector,
    bscConnector,
    bloctoConnector,
    ledgerConnector,
    trustWalletConnector,
  ],
})

export const CHAIN_IDS = chains.map((c) => c.id)

export const isChainSupported = memoize((chainId: number) => CHAIN_IDS.includes(chainId))
export const isChainTestnet = memoize((chainId: number) => chains.find((c) => c.id === chainId)?.testnet)
